<script setup lang="ts">
type TemplateSource = 'os' | 'marketplace' | 'byo'

/**
 * Where the image comes from: an operating system, a marketplace app, or one of
 * the customer's own images.
 *
 * Tabs with MANUAL activation: the arrows move focus, Enter or Space switches.
 * Switching is not free here — each tab remembers its own choice, so changing
 * tab changes what is being bought and reprices it — and a customer arrowing
 * past "Marketplace" on the way to "Minhas imagens" must not have done that.
 *
 * The counts say how much is behind each tab before anyone opens it: "105
 * apps" is the marketplace's best argument, and it was hidden behind a word.
 */
const props = defineProps<{
  source: TemplateSource
  allowByo: boolean
  counts: { families: number; versions: number; apps: number; images: number }
  /** Ids the shell gives its tab panel, for `aria-controls`. */
  panelId: string
  tabIdPrefix: string
}>()

const emit = defineEmits<{ select: [source: TemplateSource] }>()

const { t } = useI18n()

const tabs = computed(() => [
  {
    value: 'os' as const,
    icon: 'i-lucide-server',
    label: t('billing.page.checkout.os.tabOs'),
    count: props.counts.families,
    description: t(
      'billing.page.checkout.os.catalog.tabs.os',
      { families: props.counts.families, versions: props.counts.versions },
    ),
  },
  {
    value: 'marketplace' as const,
    icon: 'i-lucide-store',
    label: t('billing.page.checkout.os.tabMarketplace'),
    count: props.counts.apps,
    description: t('billing.page.checkout.os.catalog.tabs.marketplace', { count: props.counts.apps }, props.counts.apps),
  },
  ...(props.allowByo
    ? [
        {
          value: 'byo' as const,
          icon: 'i-lucide-hard-drive',
          label: t('billing.page.checkout.os.tabByo'),
          count: props.counts.images || null,
          description: t('billing.page.checkout.os.tabByoDesc'),
        },
      ]
    : []),
])

const list = ref<HTMLElement | null>(null)

function onKeydown(event: KeyboardEvent) {
  const buttons = Array.from(list.value?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [])
  const index = buttons.indexOf(event.target as HTMLElement)
  if (index === -1) return
  const next =
    event.key === 'ArrowRight'
      ? buttons[(index + 1) % buttons.length]
      : event.key === 'ArrowLeft'
        ? buttons[(index - 1 + buttons.length) % buttons.length]
        : event.key === 'Home'
          ? buttons[0]
          : event.key === 'End'
            ? buttons[buttons.length - 1]
            : null
  if (!next) return
  event.preventDefault()
  next.focus()
}
</script>

<template>
  <div
    ref="list"
    role="tablist"
    :aria-label="$t('billing.page.checkout.os.sourceLabel')"
    :class="[
      'grid gap-1 rounded-xl bg-elevated/70 p-1 ring-1 ring-inset ring-default',
      tabs.length === 2 ? 'grid-cols-2' : 'grid-cols-3',
    ]"
    @keydown="onKeydown"
  >
    <!-- An unselected tab reads `toned`, not `muted`: muted text measures
         4.3:1 on this tinted track, under the 4.5:1 small text needs. -->
    <button
      v-for="tab in tabs"
      :id="`${tabIdPrefix}-${tab.value}`"
      :key="tab.value"
      type="button"
      role="tab"
      data-testid="template-source-tab"
      :data-source="tab.value"
      :aria-selected="source === tab.value"
      :aria-controls="panelId"
      :tabindex="source === tab.value ? 0 : -1"
      :class="[
        'flex min-w-0 flex-col items-center gap-1 rounded-lg px-2 py-2 text-center transition @lg:flex-row @lg:items-start @lg:gap-2.5 @lg:px-3 @lg:text-left',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        source === tab.value
          ? 'bg-default text-highlighted shadow-sm ring-1 ring-default'
          : 'text-toned hover:bg-default/60 hover:text-highlighted',
      ]"
      @click="emit('select', tab.value)"
    >
      <UIcon
        :name="tab.icon"
        :class="['size-4.5 shrink-0 @lg:mt-0.5', source === tab.value ? 'text-primary' : '']"
      />
      <span class="min-w-0">
        <span class="flex flex-wrap items-center justify-center gap-x-1.5 text-xs font-semibold leading-4 @lg:justify-start @lg:text-sm @lg:leading-5">
          {{ tab.label }}
          <span
            v-if="tab.count"
            class="rounded-full bg-elevated px-1.5 text-[10px] font-medium tabular-nums leading-4 text-toned"
          >{{ tab.count }}</span>
        </span>
        <span
          :class="['mt-0.5 hidden text-[11px] leading-4 @2xl:block', source === tab.value ? 'text-muted' : 'text-toned']"
        >{{ tab.description }}</span>
      </span>
    </button>
  </div>
</template>
